import { useRef, useState } from 'react'

import { Box, InfiniteScroll, dimensions } from '@factorialco/deprecated-design-system'
import uniqBy from 'lodash/uniqBy'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { TAB_NAME } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/consts'

import { Item } from './components/Item'
import { Loading } from './components/Loading'
import { ItemData, Type } from './types'

interface Props {
  fetch: (itemsPerPage: number, lastCursor?: string) => void
  data: ItemData[]
  loading: boolean
  type: Type
  count: number
  pagination: {
    hasNextPage: boolean
    endCursor: string
    totalCount: number
  }
}

const ITEMS_PER_PAGE = 10

export const Base = ({ fetch, data, type, loading, count, pagination }: Props) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [lastCursor, setLastCursor] = useState<string | undefined>()
  const [hasScroll, setHasScroll] = useState(false)
  const [items, setItems] = useState<ItemData[]>([])
  const ref = useRef<HTMLDivElement>(null)

  const itemsPerPage = count - items.length > ITEMS_PER_PAGE ? ITEMS_PER_PAGE : count - items.length

  useUnsafeEffect(() => {
    if (ref.current) {
      setHasScroll(ref.current.scrollHeight > ref.current.clientHeight)
    }
  }, [ref])

  useUnsafeEffect(() => {
    fetch(itemsPerPage, lastCursor)

    track(trackingEvents.accounting.newFinancialWorkspace.workforceHistory.sectionOpened, {
      source: TAB_NAME,
      type,
    })
  }, [])

  useUnsafeEffect(() => {
    if (data) {
      const newUniqueItems = data.filter((e) => !items.find((i) => i.id === e.id))

      setItems(newUniqueItems.length ? uniqBy([...items, ...newUniqueItems], 'id') : items)
    }
  }, [data])

  const loadMore = () => {
    if (lastCursor !== pagination.endCursor) {
      setLastCursor(pagination.endCursor)
      setCurrentPage((prev) => prev + 1)

      fetch(itemsPerPage, pagination.endCursor)
    }
  }

  return (
    <Box
      maxHeight="s400"
      overflowY="auto"
      gap="s8"
      ref={ref}
      {...(hasScroll && { style: { paddingRight: dimensions.s8 } })}
    >
      {items.length > 0 && (
        <InfiniteScroll
          initialLoad={false}
          scrollContainerRef={ref}
          useWindow={false}
          pageStart={currentPage}
          hasMore={pagination.hasNextPage}
          loadMore={loadMore}
          loader={<Loading itemsPerPage={itemsPerPage} />}
        >
          <Box flexDirection="column" gap="s8">
            {items.map((item) => (
              <Item key={item.id} item={item} type={type} />
            ))}
          </Box>
        </InfiniteScroll>
      )}
      {loading && items.length === 0 && <Loading itemsPerPage={itemsPerPage} />}
    </Box>
  )
}
